import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
  TextField,
  CircularProgress,
  MenuItem,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState, useEffect } from "react";
import { tokens } from "../../../theme";
import type { IEquipment } from "../../../types/equipment";
import apiService from "../../../service/Api";

interface EquipmentIssue {
  id: number;
  issue_type: string;
  description?: string | null;
  severity?: string | null;
  status?: string | null;
}

interface ScheduleMaintenanceDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ScheduleMaintenanceFormData) => Promise<void>;
  equipment: IEquipment | null;
}

export interface ScheduleMaintenanceFormData {
  maintenanceDate: string;
  performedBy: string;
  notes: string;
  cost: string;
  nextDueDate: string;
  issueId: string; // optional link to an existing issue
}

const initialForm: ScheduleMaintenanceFormData = {
  maintenanceDate: new Date().toISOString().split("T")[0],
  performedBy: "",
  notes: "",
  cost: "",
  nextDueDate: "",
  issueId: "",
};

export default function ScheduleMaintenanceDialog({
  open,
  onClose,
  onSubmit,
  equipment,
}: ScheduleMaintenanceDialogProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [form, setForm] = useState<ScheduleMaintenanceFormData>(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [issues, setIssues] = useState<EquipmentIssue[]>([]);
  const [issuesLoading, setIssuesLoading] = useState(false);

  // Fetch open issues for this equipment when dialog opens
  useEffect(() => {
    if (!open || !equipment?.id) return;

    const fetchIssues = async () => {
      try {
        setIssuesLoading(true);
        const res = await apiService.get<{
          count: number;
          results: EquipmentIssue[];
        }>(
          `/api/equipments/equipment-issues/?equipment=${equipment.id}&status=open&page_size=50`,
        );
        setIssues(res.data.results || []);
      } catch {
        setIssues([]);
      } finally {
        setIssuesLoading(false);
      }
    };

    fetchIssues();
  }, [open, equipment?.id]);

  const handleClose = () => {
    setForm(initialForm);
    onClose();
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSubmit(form);
      setForm(initialForm);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "30px",
      backgroundColor: colors.primary[800],
      fontSize: 13,
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": {
        border: `1px solid ${colors.primary[400]}`,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 20px",
      "&::placeholder": { color: theme.palette.text.secondary, opacity: 0.8 },
    },
  };

  const selectSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "30px",
      backgroundColor: colors.primary[800],
      fontSize: 13,
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": {
        border: `1px solid ${colors.primary[400]}`,
      },
    },
    "& .MuiSelect-select": {
      padding: "12px 20px",
    },
  };

  const textAreaSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "16px",
      backgroundColor: colors.primary[800],
      fontSize: 13,
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": {
        border: `1px solid ${colors.primary[400]}`,
      },
    },
  };

  const isDisabled =
    !form.maintenanceDate || !form.performedBy.trim() || isSubmitting;

  const getIssueLabel = (issue: EquipmentIssue) => {
    const parts = [issue.issue_type];
    if (issue.severity) parts.push(`• ${issue.severity}`);
    if (issue.status) parts.push(`(${issue.status})`);
    return parts.join(" ");
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          backgroundColor: colors.primary[900],
          boxShadow: "0px 10px 40px rgba(0,0,0,0.08)",
        },
      }}
    >
      <IconButton
        onClick={handleClose}
        size="small"
        sx={{
          position: "absolute",
          right: 20,
          top: 20,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "50%",
          width: 26,
          height: 26,
          color: theme.palette.text.primary,
          zIndex: 10,
        }}
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>

      <DialogContent sx={{ p: "24px", overflowX: "hidden" }}>
        <Typography
          variant="h5"
          fontWeight={600}
          color={colors.primary[400]}
          mb={3}
        >
          Log Maintenance
        </Typography>

        {equipment && (
          <Box
            sx={{
              borderRadius: "16px",
              border: `1px solid ${theme.palette.divider}`,
              p: "16px 20px",
              mb: 3,
            }}
          >
            <Typography
              fontWeight={600}
              fontSize={14}
              color={theme.palette.text.primary}
            >
              {equipment.gear_name}
            </Typography>
            <Typography fontSize={13} color={theme.palette.text.secondary}>
              {equipment.brand_model?.brand_name} • {equipment.serial_number}
            </Typography>
          </Box>
        )}

        {/* Link to Issue (optional) */}
        <Typography
          variant="body2"
          color={theme.palette.text.secondary}
          display="block"
          mb={1}
        >
          Link to Issue{" "}
          <span style={{ color: theme.palette.text.disabled, fontSize: 11 }}>
            (optional)
          </span>
        </Typography>
        <TextField
          select
          fullWidth
          value={form.issueId}
          onChange={(e) => setForm((p) => ({ ...p, issueId: e.target.value }))}
          sx={{ ...selectSx, mb: 2 }}
          disabled={issuesLoading}
          SelectProps={{
            displayEmpty: true,
            MenuProps: {
              PaperProps: {
                sx: {
                  bgcolor: colors.primary[800],
                  borderRadius: "12px",
                  mt: 0.5,
                },
              },
            },
          }}
        >
          <MenuItem value="">
            <Typography fontSize={13} color={theme.palette.text.secondary}>
              {issuesLoading
                ? "Loading issues..."
                : "None"}
            </Typography>
          </MenuItem>
          {issues.map((issue) => (
            <MenuItem key={issue.id} value={String(issue.id)}>
              <Typography fontSize={13}>{getIssueLabel(issue)}</Typography>
            </MenuItem>
          ))}
        </TextField>

        <Typography
          variant="body2"
          color={theme.palette.text.secondary}
          display="block"
          mb={1}
        >
          Maintenance Date <span style={{ color: "#E24B4A" }}>*</span>
        </Typography>
        <TextField
          type="date"
          fullWidth
          value={form.maintenanceDate}
          onChange={(e) =>
            setForm((p) => ({ ...p, maintenanceDate: e.target.value }))
          }
          sx={{ ...fieldSx, mb: 2 }}
        />

        <Typography
          variant="body2"
          color={theme.palette.text.secondary}
          display="block"
          mb={1}
        >
          Performed By <span style={{ color: "#E24B4A" }}>*</span>
        </Typography>
        <TextField
          fullWidth
          placeholder="e.g. Nigel Packer"
          value={form.performedBy}
          onChange={(e) =>
            setForm((p) => ({ ...p, performedBy: e.target.value }))
          }
          sx={{ ...fieldSx, mb: 2 }}
        />

        <Typography
              variant="body2"
              color={theme.palette.text.secondary}
              display="block"
              mb={1}
            >
              Cost
            </Typography>
            <TextField
              fullWidth
              type="number"
              placeholder="0.00"
              value={form.cost}
              onChange={(e) => setForm((p) => ({ ...p, cost: e.target.value }))}
              sx={fieldSx}
            />

        {/* <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} mb={2}>
          <Box>
            <Typography
              variant="body2"
              color={theme.palette.text.secondary}
              display="block"
              mb={1}
            >
              Cost
            </Typography>
            <TextField
              fullWidth
              type="number"
              placeholder="0.00"
              value={form.cost}
              onChange={(e) => setForm((p) => ({ ...p, cost: e.target.value }))}
              sx={fieldSx}
            />
          </Box>
          <Box>
            <Typography
              variant="body2"
              color={theme.palette.text.secondary}
              display="block"
              mb={1}
            >
              Next Due Date
            </Typography>
            <TextField
              type="date"
              fullWidth
              value={form.nextDueDate}
              onChange={(e) =>
                setForm((p) => ({ ...p, nextDueDate: e.target.value }))
              }
              sx={fieldSx}
            />
          </Box>
        </Box> */}

        <Typography
          variant="body2"
          color={theme.palette.text.secondary}
          display="block"
          mb={1}
        >
          Notes
        </Typography>
        <TextField
          multiline
          rows={3}
          fullWidth
          placeholder="Describe what was done or needs to be checked..."
          value={form.notes}
          onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
          sx={{ ...textAreaSx, mb: 4 }}
        />

        <Box display="flex" justifyContent="flex-end">
          <button
            onClick={handleSubmit}
            disabled={isDisabled}
            style={{
              background: colors.primary[400],
              color: colors.primary[900],
              border: "none",
              borderRadius: "12px",
              padding: "14px 28px",
              fontSize: 15,
              fontWeight: 500,
              cursor: isDisabled ? "not-allowed" : "pointer",
              opacity: isDisabled ? 0.6 : 1,
              display: "flex",
              alignItems: "center",
              gap: 8,
              transition: "opacity 0.2s ease",
            }}
          >
            {isSubmitting && <CircularProgress size={16} color="inherit" />}
            {isSubmitting ? "Saving..." : "Log Maintenance"}
          </button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
